import type { GeoPackageContent, GeoPackageContentPreflight } from './geopackageCore';
import type { GeoPackagePreflightSnapshot } from './geopackageImport';
import { GEOPACKAGE_LIMITS } from './geopackageLimits';

export const GEOPACKAGE_PREFLIGHT_LABELS = {
  ready: 'Ready',
  'too-many-records': 'Too many records',
  'too-many-fields': 'Too many fields',
  'too-many-cells': 'Too many cells',
  'geometry-too-large': 'Geometry too large',
  'unsupported-geometry': 'Unsupported geometry',
  'unsupported-crs': 'Unsupported CRS',
  'estimated-data-too-large': 'Estimated data too large',
  'check-failed': 'Check failed',
} as const;

export function isGeoPackageContentReady(
  content: GeoPackageContent,
  preflight: GeoPackagePreflightSnapshot,
): boolean {
  return content.kind !== 'unsupported' && preflight[content.tableName]?.status === 'ready';
}

export function formatGeoPackageApproximateBytes(bytes: number): string {
  if (bytes < 1_000_000) return `~${Math.round(bytes / 1_000).toLocaleString()} KB`;
  return `~${(bytes / 1_000_000).toLocaleString(undefined, { maximumFractionDigits: 1 })} MB`;
}

export interface GeoPackageSelectionSummary {
  readonly contents: number;
  readonly records: number;
  readonly cells: number;
  readonly approximateBytes: number;
  readonly checking: number;
  readonly issue: string | null;
}

export function summarizeGeoPackageSelection(
  contents: readonly GeoPackageContent[],
  preflight: GeoPackagePreflightSnapshot,
  selectedNames: readonly string[],
): GeoPackageSelectionSummary {
  const selected = new Set(selectedNames);
  let records = 0;
  let cells = 0;
  let approximateBytes = 0;
  let checking = 0;
  for (const content of contents) {
    if (content.kind !== 'unsupported' && !preflight[content.tableName]) checking += 1;
    if (!selected.has(content.tableName)) continue;
    const value: GeoPackageContentPreflight | undefined = preflight[content.tableName];
    if (!value || value.status !== 'ready') continue;
    records += value.recordCount;
    cells += value.cellCount;
    approximateBytes += value.approximateBytes;
  }
  const issue =
    selected.size > GEOPACKAGE_LIMITS.maximumSelectedContents
      ? `Select no more than ${GEOPACKAGE_LIMITS.maximumSelectedContents} contents. Deselect one or more.`
      : records > GEOPACKAGE_LIMITS.maximumTotalRows
        ? `Selected contents exceed the ${GEOPACKAGE_LIMITS.maximumTotalRows.toLocaleString()}-record import limit. Deselect one or more.`
        : cells > GEOPACKAGE_LIMITS.maximumCells
          ? `Selected contents exceed the ${GEOPACKAGE_LIMITS.maximumCells.toLocaleString()}-cell import limit. Deselect one or more.`
          : approximateBytes > GEOPACKAGE_LIMITS.maximumDecodedBytes
            ? 'Estimated selected data exceeds the 128 MB import limit. Deselect one or more.'
            : null;
  return {
    contents: selected.size,
    records,
    cells,
    approximateBytes,
    checking,
    issue,
  };
}
